import React, { FC, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { useMount } from "react-use";

import { StandardLayout } from "@/components/layouts";
import { DetailsPage } from "@/components/templates";
import { invoiceApi } from "@/shared/apis";
import { DetailPageState, InvoiceCategory, Permissions } from "@/shared/domain";
import { useBreadcrumb, useNavigation, useRouterHook } from "@/shared/hooks";
import { useAuth } from "@/shared/hooks/useAuth";
import { useMediaDisplayer } from "@/shared/hooks/useMediaDisplayer";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import InvoiceCard from "../components/InvoiceCard/InvoiceCard";
import { StyledInvoiceDetailsPage, StyledPdfReader } from "./styled";
import InvoiceDetailsPanel from "./TabPanels/InvoiceDetailsPanel";
import { mapInvoice } from "./utils";

const InvoiceDetailPage: FC = () => {
    const { getStateOrSearch } = useRouterHook<DetailPageState>();
    const { setPageTitle } = useBreadcrumb();
    const { id: invoiceId } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const { navToInvoices } = useNavigation();
    const { user } = useAuth();

    const policyholderId = getStateOrSearch("policyholderId");

    const ibpAclId = user.getIbpAclId(policyholderId);

    const canAccessInvoicePdf = user.hasPermission(Permissions.ViewInvoicePdf, policyholderId);

    const { data: invoiceDetails, isFetched } = useQuery(
        ["invoices", { invoiceId }],
        () => invoiceApi.getInvoiceDetails(+invoiceId, +policyholderId),
        {
            enabled: !!policyholderId,
        },
    );
    const { incUsageCount } = useUserFeedback();
    const { displaySizedMode } = useMediaDisplayer();

    useEffect(() => {
        if (!isFetched && policyholderId == null) {
            navToInvoices(InvoiceCategory.ALL);
        }

        if (invoiceDetails) {
            setPageTitle(
                `${invoiceDetails.paymentDetails.reference} ${invoiceDetails.contractDetails.contractBranch.name}`,
            );
            if (canAccessInvoicePdf) {
                displaySizedMode({
                    title: `${invoiceDetails.contractDetails.contractBranch.name} - ${invoiceDetails.policyHolder}`,
                    type: "pdf",
                    url: invoiceApi.getInvoicePdfUrl(invoiceDetails.invoiceId, ibpAclId),
                    elementId: "invoice_pdf",
                });
            }
        }
    }, [isFetched]);

    useMount(() => {
        incUsageCount();
        tagManager.pushViewItemEvent("invoices", "Invoices");
    });

    const panels = [{ label: t("Common.Details"), content: <InvoiceDetailsPanel invoice={invoiceDetails} /> }];

    return (
        <StandardLayout title={t("PageTitles.InvoiceDetails")}>
            <StyledInvoiceDetailsPage>
                <DetailsPage
                    panels={panels}
                    objectType="invoice"
                    card={isFetched && <InvoiceCard invoice={mapInvoice(invoiceDetails, +policyholderId)} />}
                    extraContent={canAccessInvoicePdf && <StyledPdfReader id="invoice_pdf" data-testid="invoice_pdf" />}
                />
            </StyledInvoiceDetailsPage>
        </StandardLayout>
    );
};

export default InvoiceDetailPage;
